/**
 * UI preference store. Sidebar collapsed, theme, density. Persisted to
 * localStorage (these are stable user prefs — outlive the tab).
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type Theme = 'system' | 'light' | 'dark'
export type Density = 'compact' | 'comfortable'

interface UiState {
  sidebarCollapsed: boolean
  theme: Theme
  density: Density
  commandPaletteOpen: boolean
  /** Off-canvas nav drawer below `lg`. Never persisted. */
  mobileNavOpen: boolean

  toggleSidebar: () => void
  setSidebarCollapsed: (v: boolean) => void
  setTheme: (t: Theme) => void
  setDensity: (d: Density) => void
  setCommandPaletteOpen: (v: boolean) => void
  setMobileNavOpen: (v: boolean) => void
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: 'system',
      density: 'compact',
      commandPaletteOpen: false,
      mobileNavOpen: false,

      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      setTheme: (t) => set({ theme: t }),
      setDensity: (d) => set({ density: d }),
      setCommandPaletteOpen: (v) => set({ commandPaletteOpen: v }),
      setMobileNavOpen: (v) => set({ mobileNavOpen: v }),
    }),
    {
      name: 'admindash-v2-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        theme: s.theme,
        density: s.density,
      }),
    },
  ),
)

/** Apply theme + density to <html> data attrs. Call once on mount + when
 * theme/density change. Side effects only — no return. */
export function applyUiPrefs(): void {
  const { theme, density } = useUiStore.getState()
  const root = document.documentElement
  if (theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }
  document.body.setAttribute('data-density', density)
}
